import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { filterDataToParams, paramsToFilterData } from './searchHelper';
import { FacetType, FilterData } from './searchModel';
import { useUserPreferences } from '../../api/swrApi';
import { Api } from '../../types/apiSpec';
import { Role, useRole } from '../../context/SessionContext';
import { TAB_URI_KEY } from '../../hooks/useActiveTab';
import { getDecodedHashParams } from '../hashUtils';
import { replaceNonAlphanumeric } from '../replaceNonAlphanumeric';
import { TabConfig } from '../../../components/app/compound/Learning/Learning.types';
import { searchParamsToString } from '../URL/searchParamsToString';
import { useViewContext } from '../../context/ViewContext';

const DEFAULT_FILTERS: FilterData<typeof FacetType.Checkbox> = { facets: [], page: 1 };

export const useSearchState = (tabConfig: TabConfig[] | undefined, hideOverdue: boolean, learning?: boolean) => {
  const { hash, search } = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(search);
  const { app } = useViewContext() ?? {};
  const hashParams = getDecodedHashParams(hash);
  const [activeTab, setActiveTab] = useState(
    tabConfig?.find(({ title }) => replaceNonAlphanumeric(title) === hashParams.get(TAB_URI_KEY)) ||
      tabConfig?.find(({ isDefault }) => isDefault) ||
      tabConfig?.[0],
  );
  const storedFilters = useRef<Record<string, string>>({});

  const role = useRole();

  const {
    data: userPreferences,
    error,
    isLoading,
  } = useUserPreferences({
    shouldFetch: role && !search && !!app.user?.userId && !!activeTab?.filterGroups.includes(Api.FacetName.disciplines),
    userid: app.user?.userId ?? '',
  });

  useEffect(() => {
    if (!userPreferences) {
      return;
    }

    const { interestAreas, disciplines } = userPreferences;

    if (interestAreas.length) {
      searchParams.set(Api.FacetName.interestAreas, interestAreas.join(','));
    }

    if (disciplines.length) {
      searchParams.set(Api.FacetName.disciplines, disciplines.join(','));
    }

    history.replace({ search: searchParams.toString(), hash: hashParams.toString() });
  }, [userPreferences]);

  useEffect(() => {
    if (activeTab && learning) {
      hashParams.set(TAB_URI_KEY, replaceNonAlphanumeric(activeTab.title));
      history.replace({ hash: hashParams.toString(), search: searchParamsToString(searchParams, false) });
    }
  }, []);

  const urlFilters = useMemo(() => paramsToFilterData(search, activeTab?.filterGroups ?? [], hideOverdue), [search]);

  const updateFilters = (filterData: FilterData<typeof FacetType.Checkbox>, page?: number) => {
    history.replace({
      search: filterDataToParams({
        ...filterData,
        page: page || 1,
      }),
      hash: hashParams.toString(),
    });
  };

  const handleChangeTab = (id: string) => {
    const tab = tabConfig?.find(t => t.uniqueId === id);

    if (tab && activeTab) {
      setActiveTab(tab);
      storedFilters.current[activeTab.uniqueId] = search;

      hashParams.set(TAB_URI_KEY, replaceNonAlphanumeric(tab.title));

      history.replace({ search: storedFilters.current[tab.uniqueId] || '', hash: hashParams.toString() });
    }
  };

  const clearFilters = () => {
    if (activeTab) {
      hashParams.set(TAB_URI_KEY, activeTab.title);
    }
    history.replace({ search: filterDataToParams(DEFAULT_FILTERS), hash: hashParams.toString() });
  };

  if (hideOverdue && searchParams.get(Api.Param.SortBy) === Api.SortBy.dueDate) {
    updateFilters(urlFilters, Number(searchParams.get(Api.Param.Page) || 1));
  }

  return {
    urlFilters,
    updateFilters,
    clearFilters,
    paramsReady:
      role === Role.GUEST ||
      !!search ||
      (userPreferences && Object.keys(userPreferences).length === 0) ||
      (!search && !activeTab?.filterGroups.includes(Api.FacetName.disciplines)) ||
      !!error,
    isInitialTeamsLoading: isLoading,
    activeTab,
    handleChangeTab,
  };
};
